import { useMemo } from 'react';

/**
 * useEnrichedOpportunities
 *
 * Shared enrichment for opportunity arrays before they are handed to
 * PipelineBoard / OpportunityPipeline. Resolves display fields from
 * denormalized opportunity data with fallback lookups via customers
 * and users collections so both DashboardPage and OppPage produce
 * identical objects.
 *
 * This hook does NOT apply RBAC filtering — callers are responsible
 * for restricting `opportunities` based on permissions before passing
 * them in. Enrichment is purely cosmetic (display names, labels).
 *
 * @param {Array}  opportunities - Raw opportunity objects from Firestore
 * @param {Object} [options]
 * @param {Array}  [options.customers] - Optional customers list for name fallback
 * @param {Array}  [options.users]     - Optional users list for assigned-rep name fallback
 * @returns {Array} Enriched opportunities with customerName, assignedUserName,
 *                  opportunityNumber, and leadSource resolved.
 */
export default function useEnrichedOpportunities(opportunities, { customers = [], users = [] } = {}) {
    const customersById = useMemo(() => {
        const m = new Map();
        customers.forEach(c => {
            if (c?.id) m.set(c.id, c);
            if (c?.customerId) m.set(c.customerId, c);
        });
        return m;
    }, [customers]);

    const usersById = useMemo(() => {
        const m = new Map();
        users.forEach(u => {
            if (u?.uid) m.set(u.uid, u);
            if (u?.id) m.set(u.id, u);
        });
        return m;
    }, [users]);

    return useMemo(() => {
        if (!Array.isArray(opportunities)) return [];

        return opportunities.map(opp => {
            // Customer name: prefer denormalized, fall back to lookup
            const customer =
                customersById.get(opp.customerId) ||
                customersById.get(opp.customer_id) ||
                null;
            const customerName =
                opp.customerName ||
                customer?.customerName ||
                customer?.name ||
                'Unknown Customer';

            // Assigned user name: prefer users lookup, fall back to denormalized salesReps
            const assignedUser = usersById.get(opp.uid);
            let assignedUserName;
            if (assignedUser) {
                const fullName = `${assignedUser.firstName || ''} ${assignedUser.lastName || ''}`.trim();
                assignedUserName =
                    fullName ||
                    assignedUser.displayName ||
                    assignedUser.email ||
                    assignedUser.uid;
            } else {
                assignedUserName =
                    opp.salesReps?.[0]?.salesRepName ||
                    opp.salesRepNames?.[0] ||
                    'Unassigned';
            }

            return {
                ...opp,
                opportunityNumber:
                    opp.opportunityNumber ||
                    opp.number ||
                    opp.oppNumber ||
                    opp.oppNum ||
                    '-',
                leadSource: opp.leadSource || opp.source || opp.lead_source || '-',
                customerName,
                assignedUserName,
            };
        });
    }, [opportunities, customersById, usersById]);
}
